import { Add, ArrowDropDown, Delete, Edit, SocialDistanceOutlined } from "@mui/icons-material";
import { Box, Button, Checkbox, CircularProgress, Divider, ListItemIcon, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from "@mui/material";
import { SecaoConfiguracao } from "../../../../../core/components/SecaoConfiguracao";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { useEffect, useState } from "react";
import { useMarketplaces } from "../../../../hooks/useMarketplaces";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { useDataHora } from "../../../../../core/hooks/useDataHora";
import { ConfirmarAcaoTextoModal } from "../../../../../core/components/ConfirmarAcaoTextoModal";
import { IDistancia } from "../../../../../pedidos/interface/IDistancia";
import { FormularioDistanciasEntrega } from "../../../../../configuracoes/containers/FormularioDistanciasEntrega";

interface DistanciasEntregaMarketplacePaperProps {
    marketplace: IMarketplace
    refresh: () => void;
    isLoading: boolean;
}

export const DistanciasEntregaMarketplacePaper = ({ marketplace, refresh, isLoading }: DistanciasEntregaMarketplacePaperProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openAcoes = Boolean(anchorEl);

    const abrirAcoes = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const fecharAcoes = () => {
        setAnchorEl(null);
    };

    const { salvar, loading } = useMarketplaces();

    const [distanciasSelecionadas, setDistanciasSelecionadas] = useState<IDistancia[]>([]);
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);
    const [page, setPage] = useState<number>(0);

    const { real } = useMoeda();
    const { dataHoraBr } = useDataHora();

    const [formularioAberto, setFormularioAberto] = useState(false);
    const abrirFormulario = () => setFormularioAberto(true);
    const fecharFormulario = () => {
        setFormularioAberto(false);
        setDistanciaEdicao(undefined);
    }

    const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
    const abrirConfirmacao = () => setConfirmacaoAberta(true);
    const fecharConfirmacao = () => setConfirmacaoAberta(false);

    const [distanciaEdicao, setDistanciaEdicao] = useState<IDistancia | undefined>(undefined);
    const abrirEdicao = (distancia?: IDistancia) => {
        setDistanciaEdicao(distancia);
        abrirFormulario();
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const selecionarDistancia = (distancia: IDistancia) => {
        const selectedIndex = distanciasSelecionadas.findIndex(s => s.uuid === distancia.uuid);
        let newSelected: IDistancia[] = [];

        if (selectedIndex === -1) {
            newSelected = [...distanciasSelecionadas, distancia];
        } else {
            newSelected = [...distanciasSelecionadas.slice(0, selectedIndex), ...distanciasSelecionadas.slice(selectedIndex + 1)];
        }

        setDistanciasSelecionadas(newSelected);
    };

    const limparDistanciasSelecionadas = () => setDistanciasSelecionadas([]);

    const distanciasPaginadas = marketplace?.configuracao?.distanciasEntrega?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    ) ?? [];

    useEffect(() => {
        limparDistanciasSelecionadas();
    }, [])

    const cadastrarDistancia = async (distancia: IDistancia) => {
        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                distanciasEntrega: [...(marketplace.configuracao?.distanciasEntrega ?? []), distancia]
            }
        })

        refresh();
        return distancia;
    }

    const editarDistancia = async (distancia: IDistancia) => {
        const distanciaIndex = marketplace?.configuracao?.distanciasEntrega?.findIndex(r => r.uuid === distancia.uuid);

        if (distanciaIndex === -1 || distanciaIndex === undefined) {
            return false;
        }

        const distanciasAtualizadas = [...(marketplace.configuracao?.distanciasEntrega ?? [])];
        distanciasAtualizadas[distanciaIndex] = distancia;

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                distanciasEntrega: distanciasAtualizadas,
            },
        });

        limparDistanciasSelecionadas();
        refresh();
        fecharAcoes();
        return true;
    };


    const excluirDistancias = async () => {
        const distanciasRestantes = marketplace.configuracao?.distanciasEntrega?.filter(
            distancia => !distanciasSelecionadas.some(r => r.uuid === distancia.uuid)
        ) ?? [];

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                distanciasEntrega: distanciasRestantes
            }
        });

        limparDistanciasSelecionadas();
        fecharAcoes();
        refresh();
        fecharConfirmacao();
    };


    return (
        <>
            <FormularioDistanciasEntrega
                formularioAberto={formularioAberto}
                fecharFormulario={fecharFormulario}
                cadastrarDistancia={cadastrarDistancia}
                editarDistancia={editarDistancia}
                valoresIniciais={distanciaEdicao}
                habilitarEdicao={distanciaEdicao ? true : false}
            />
            <ConfirmarAcaoTextoModal
                titulo={`Excluir ${distanciasSelecionadas.length == 1 ? "distância" : "distâncias"}`}
                mensagem={<Typography>Para confirmar a exclusão, digite <b>Confirmar</b> no campo.</Typography>}
                cancelar={fecharConfirmacao}
                confirmar={excluirDistancias}
                open={confirmacaoAberta}
                parametro="Confirmar"
            />
            <SecaoConfiguracao
                titulo="Distâncias de entrega"
                descricao="Faixas de distância e o valor do frete de cada uma"
                icone={<SocialDistanceOutlined />}
                acoes={
                    <>
                        <Button
                            disabled={distanciasSelecionadas.length <= 0}
                            variant="contained"
                            endIcon={
                                <ArrowDropDown
                                    sx={{ transform: openAcoes ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                                />
                            }
                            onClick={abrirAcoes}
                        >
                            Ações
                        </Button>
                        <Menu
                            id={`acoes-distancias-menu`}
                            MenuListProps={{
                                'aria-labelledby': `acoes-distancias-menu`,
                            }}
                            anchorEl={anchorEl}
                            open={openAcoes}
                            onClose={fecharAcoes}
                        >
                            <MenuItem disabled>
                                <Typography variant="inherit" noWrap>
                                    {distanciasSelecionadas?.length} {distanciasSelecionadas?.length == 1 ? "distância" : "distâncias"} {distanciasSelecionadas?.length == 1 ? "selecionada" : "selecionadas"}
                                </Typography>
                            </MenuItem>
                            <Divider />
                            <Tooltip title='Edite a distância'>
                                <MenuItem onClick={() => abrirEdicao(distanciasSelecionadas[0])} disabled={distanciasSelecionadas?.length != 1}>
                                    <ListItemIcon>
                                        <Edit fontSize="small" />
                                    </ListItemIcon>
                                    <Typography variant="inherit" noWrap>
                                        Editar
                                    </Typography>
                                </MenuItem>
                            </Tooltip>
                            <Tooltip title='Exclua a distância'>
                                <MenuItem onClick={abrirConfirmacao} disabled={distanciasSelecionadas?.length < 1}>
                                    <ListItemIcon>
                                        <Delete fontSize="small" />
                                    </ListItemIcon>
                                    <Typography variant="inherit" noWrap>
                                        Excluir
                                    </Typography>
                                </MenuItem>
                            </Tooltip>
                        </Menu>
                        <Button
                            disabled={distanciasSelecionadas.length > 0}
                            onClick={abrirFormulario}
                            variant="contained"
                            endIcon={<Add />}
                        >
                            Cadastrar
                        </Button>
                    </>
                }
            >
                <Box>
                    <TableContainer component={Box}>
                        <Table sx={{ minWidth: 650 }} size="medium">
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>
                                        De
                                    </TableCell>
                                    <TableCell>
                                        Até
                                    </TableCell>
                                    <TableCell>
                                        Valor
                                    </TableCell>
                                    <TableCell>
                                        Cadastrada às
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : distanciasPaginadas.length > 0 ? (
                                    distanciasPaginadas.map((distancia) => {
                                        const isItemSelected = distanciasSelecionadas.some(s => s.uuid === distancia.uuid);

                                        return (
                                            <TableRow
                                                key={distancia.uuid}
                                                selected={isItemSelected}
                                                onClick={() => selecionarDistancia(distancia)}
                                                hover
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {distancia.inicio}km
                                                </TableCell>
                                                <TableCell>
                                                    {distancia.fim}km
                                                </TableCell>
                                                <TableCell>
                                                    {real(distancia.valor)}
                                                </TableCell>
                                                <TableCell>{dataHoraBr(distancia.dataHora)}</TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                                            Nenhuma distância configurada
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 20]}
                            component="div"
                            count={marketplace?.configuracao?.distanciasEntrega?.length ?? 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Linhas por página"
                        />
                    </TableContainer>
                </Box>
            </SecaoConfiguracao>

        </>
    );
}